import { useState, useEffect } from 'react';
import { getFundManagers, createFundManager } from '../api';
import { useMobile } from '../components/Layout';
import { useTheme } from '../ThemeContext';
import {
  Building2, Mail, Globe, Hash, Calendar, Plus, X, Pencil,
  Search, ChevronRight,
} from 'lucide-react';
import axios from 'axios';

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000' });
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const COUNTRIES = [
  { value: 'LU', label: 'Luxembourg' }, { value: 'BE', label: 'Belgium' },
  { value: 'NL', label: 'Netherlands' }, { value: 'FR', label: 'France' },
  { value: 'DE', label: 'Germany' }, { value: 'AT', label: 'Austria' },
  { value: 'CH', label: 'Switzerland' }, { value: 'GB', label: 'United Kingdom' },
  { value: 'IE', label: 'Ireland' }, { value: 'IT', label: 'Italy' },
  { value: 'ES', label: 'Spain' }, { value: 'PT', label: 'Portugal' },
  { value: 'SE', label: 'Sweden' }, { value: 'DK', label: 'Denmark' },
  { value: 'FI', label: 'Finland' }, { value: 'NO', label: 'Norway' },
  { value: 'PL', label: 'Poland' }, { value: 'CZ', label: 'Czech Republic' },
  { value: 'US', label: 'United States' }, { value: 'CA', label: 'Canada' },
  { value: 'SG', label: 'Singapore' }, { value: 'HK', label: 'Hong Kong' },
  { value: 'JP', label: 'Japan' }, { value: 'AE', label: 'United Arab Emirates' },
  { value: 'OTHER', label: 'Other' },
];

const COUNTRY_LABEL = Object.fromEntries(COUNTRIES.map(c => [c.value, c.label]));

const getInputStyle = (theme) => ({
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: `1px solid ${theme.border}`,
  fontSize: 13,
  color: theme.textPrimary,
  background: theme.bgInput,
  boxSizing: 'border-box',
  outline: 'none',
});

const Badge = ({ children, theme }) => (
  <span style={{
    background: theme.bgInput,
    color: theme.textSecondary,
    border: `1px solid ${theme.border}`,
    padding: '3px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  }}>
    {children}
  </span>
);

const Label = ({ children, required, theme }) => (
  <label style={{
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: theme.textSecondary,
    marginBottom: 6,
  }}>
    {children} {required && <span style={{ color: '#991B1B' }}>*</span>}
  </label>
);

const Modal = ({ title, subtitle, onClose, onSubmit, submitting, error, form, setForm, theme, isMobile, isEdit }) => {
  const inputStyle = getInputStyle(theme);

  const primaryBtn = {
    background: submitting ? '#93C5FD' : '#2563EB',
    color: 'white',
    border: '1px solid transparent',
    borderRadius: 8,
    padding: '9px 15px',
    fontWeight: 600,
    fontSize: 13,
    cursor: submitting ? 'not-allowed' : 'pointer',
  };

  const secondaryBtn = {
    background: theme.bgInput,
    color: theme.textSecondary,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    padding: '9px 15px',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: theme.bgOverlay,
      display: 'flex',
      alignItems: isMobile ? 'flex-end' : 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: theme.bgCard,
        borderRadius: isMobile ? '18px 18px 0 0' : 12,
        width: '100%',
        maxWidth: isMobile ? '100%' : 500,
        maxHeight: isMobile ? '92vh' : '86vh',
        overflowY: 'auto',
        boxShadow: theme.shadowLg,
        border: isMobile ? 'none' : `1px solid ${theme.border}`,
      }}>
        <div style={{
          padding: 20,
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          background: theme.bgCard,
          zIndex: 1,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: theme.textPrimary }}>
              {title}
            </h2>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: theme.textSecondary }}>
              {subtitle}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: theme.bgInput,
            border: `1px solid ${theme.border}`,
            borderRadius: 8,
            cursor: 'pointer',
            padding: 8,
            display: 'flex',
            color: theme.textSecondary,
          }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: 20 }}>
          <div style={{ marginBottom: 14 }}>
            <Label required theme={theme}>Legal Name</Label>
            <input
              type="text"
              value={form.legal_name}
              onChange={e => setForm(d => ({ ...d, legal_name: e.target.value }))}
              placeholder="Luxe Capital SA"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 14 }}>
            <Label required theme={theme}>Contact Email</Label>
            <input
              type="email"
              value={form.contact_email}
              onChange={e => setForm(d => ({ ...d, contact_email: e.target.value }))}
              placeholder="compliance@luxecapital.lu"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 14 }}>
            <Label required theme={theme}>Country</Label>
            <select
              value={form.country}
              onChange={e => setForm(d => ({ ...d, country: e.target.value }))}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              {COUNTRIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 18 }}>
            <Label theme={theme}>CSSF Registration Number</Label>
            <input
              type="text"
              value={form.cssf_registration}
              onChange={e => setForm(d => ({ ...d, cssf_registration: e.target.value }))}
              placeholder="e.g. S00000123"
              style={inputStyle}
            />
          </div>

          {error && (
            <div style={{
              background: '#FEF2F2',
              borderRadius: 8,
              padding: '10px 13px',
              marginBottom: 14,
              fontSize: 12,
              color: '#991B1B',
              fontWeight: 600,
              border: '1px solid #FECACA',
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={onClose} style={secondaryBtn}>
              Cancel
            </button>
            <button onClick={onSubmit} disabled={submitting} style={primaryBtn}>
              {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const FundManagers = () => {
  const isMobile = useMobile();
  const { theme } = useTheme();

  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [editError, setEditError] = useState(null);

  const emptyForm = {
    legal_name: '',
    contact_email: '',
    country: 'LU',
    cssf_registration: '',
  };

  const [createForm, setCreateForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);

  const inputStyle = getInputStyle(theme);

  const primaryBtn = {
    background: '#2563EB',
    color: 'white',
    border: '1px solid #2563EB',
    borderRadius: 8,
    padding: '9px 15px',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
  };

  const secondaryIconBtn = {
    background: theme.bgInput,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    cursor: 'pointer',
    padding: 7,
    display: 'flex',
    alignItems: 'center',
    color: theme.textSecondary,
  };

  useEffect(() => {
    getFundManagers()
      .then(res => setManagers(res.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = managers.filter(manager => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      manager.legal_name?.toLowerCase().includes(q) ||
      manager.contact_email?.toLowerCase().includes(q) ||
      manager.cssf_registration?.toLowerCase().includes(q) ||
      COUNTRY_LABEL[manager.country]?.toLowerCase().includes(q)
    );
  });

  const countriesCount = new Set(managers.map(m => m.country).filter(Boolean)).size;
  const cssfCount = managers.filter(m => m.cssf_registration).length;
  const luxCount = managers.filter(m => m.country === 'LU').length;

  const handleCreate = async () => {
    if (!createForm.legal_name || !createForm.contact_email || !createForm.country) {
      setCreateError('Legal name, contact email and country are required.');
      return;
    }

    setCreating(true);
    setCreateError(null);

    try {
      await createFundManager(createForm);
      const res = await getFundManagers();
      setManagers(res.data || []);
      setShowCreate(false);
      setCreateForm(emptyForm);
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create fund manager.');
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = async () => {
    if (!editForm.legal_name || !editForm.contact_email || !editForm.country) {
      setEditError('Legal name, contact email and country are required.');
      return;
    }

    setEditing(true);
    setEditError(null);

    try {
      await API.patch(`/fund-managers/${editTarget.id}`, editForm);
      const res = await getFundManagers();
      setManagers(res.data || []);
      setShowEdit(false);
      setEditTarget(null);

      if (selected?.id === editTarget.id) {
        setSelected((res.data || []).find(m => m.id === editTarget.id) || null);
      }
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update fund manager.');
    } finally {
      setEditing(false);
    }
  };

  const openEdit = (e, manager) => {
    e.stopPropagation();
    setEditTarget(manager);
    setEditForm({
      legal_name: manager.legal_name || '',
      contact_email: manager.contact_email || '',
      country: manager.country || 'LU',
      cssf_registration: manager.cssf_registration || '',
    });
    setEditError(null);
    setShowEdit(true);
  };

  const closeCreate = () => {
    setShowCreate(false);
    setCreateForm(emptyForm);
    setCreateError(null);
  };

  const closeEdit = () => {
    setShowEdit(false);
    setEditTarget(null);
    setEditError(null);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: theme.textTertiary, fontSize: 14 }}>Loading fund managers...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        marginBottom: 24,
        flexWrap: 'wrap',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.textPrimary }}>
            Fund Managers
          </h1>
          <p style={{ margin: '5px 0 0', fontSize: 13, color: theme.textSecondary }}>
            Client entities, regulatory identifiers, and jurisdictional scope.
          </p>
        </div>

        <button onClick={() => { setShowCreate(true); setCreateError(null); }} style={primaryBtn}>
          <Plus size={15} /> Add Fund Manager
        </button>
      </div>

      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        boxShadow: theme.shadowSm,
        marginBottom: 16,
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
          borderBottom: `1px solid ${theme.border}`,
        }}>
          {[
            ['Total managers', managers.length],
            ['Luxembourg', luxCount],
            ['CSSF registered', cssfCount],
            ['Countries', countriesCount],
          ].map(([label, value], idx) => (
            <div key={label} style={{
              padding: '16px 18px',
              borderRight: !isMobile && idx < 3 ? `1px solid ${theme.border}` : 'none',
            }}>
              <p style={{ margin: 0, fontSize: 12, color: theme.textTertiary, fontWeight: 600 }}>{label}</p>
              <p style={{ margin: '6px 0 0', fontSize: 24, fontWeight: 700, color: theme.textPrimary, lineHeight: 1 }}>{value}</p>
            </div>
          ))}
        </div>

        <div style={{
          padding: '14px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}>
          <div style={{ position: 'relative', width: isMobile ? '100%' : 340 }}>
            <Search size={14} color={theme.textTertiary} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder="Search managers, countries, CSSF numbers"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, paddingLeft: 34 }}
            />
          </div>

          <span style={{ fontSize: 12, color: theme.textTertiary }}>
            Showing {filtered.length} of {managers.length}
          </span>
        </div>
      </div>

      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        boxShadow: theme.shadowSm,
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 16px',
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'center',
        }}>
          <div>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: theme.textPrimary }}>
              Manager Directory
            </p>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: theme.textTertiary }}>
              Fund management companies and regulatory metadata
            </p>
          </div>
          <Building2 size={17} color={theme.textTertiary} />
        </div>

        {filtered.length === 0 ? (
          <div style={{ padding: '56px 24px', textAlign: 'center' }}>
            <Building2 size={34} color={theme.textTertiary} style={{ marginBottom: 10 }} />
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: theme.textPrimary }}>No fund managers found</p>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textSecondary }}>Create a manager or adjust your search.</p>
          </div>
        ) : isMobile ? (
          <div style={{ display: 'grid', gap: 8, padding: 10 }}>
            {filtered.map(manager => {
              const isSelected = selected?.id === manager.id;

              return (
                <div key={manager.id} style={{
                  background: theme.bgInput,
                  border: `1px solid ${isSelected ? '#CBD5E1' : theme.border}`,
                  borderRadius: 10,
                  overflow: 'hidden',
                }}>
                  <button
                    onClick={() => setSelected(isSelected ? null : manager)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      background: 'transparent',
                      border: 'none',
                      padding: 13,
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>{manager.legal_name}</p>
                        <p style={{ margin: '3px 0 0', fontSize: 11, color: theme.textSecondary }}>{manager.contact_email}</p>
                      </div>
                      <Badge theme={theme}>{manager.country}</Badge>
                    </div>

                    <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Badge theme={theme}>{manager.cssf_registration || 'No CSSF number'}</Badge>
                      <span style={{ marginLeft: 'auto', color: theme.textTertiary }}>
                        <ChevronRight size={14} style={{ transform: isSelected ? 'rotate(90deg)' : 'none' }} />
                      </span>
                    </div>
                  </button>

                  {isSelected && (
                    <div style={{ borderTop: `1px solid ${theme.border}`, padding: 13 }}>
                      {[
                        { icon: Building2, label: 'Legal Name', value: manager.legal_name },
                        { icon: Mail, label: 'Contact Email', value: manager.contact_email },
                        { icon: Globe, label: 'Country', value: COUNTRY_LABEL[manager.country] || manager.country },
                        { icon: Hash, label: 'CSSF Registration', value: manager.cssf_registration || '-' },
                        { icon: Calendar, label: 'Registered', value: manager.created_at ? new Date(manager.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '-' },
                      ].map(({ icon: Icon, label, value }) => (
                        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0' }}>
                          <Icon size={13} color={theme.textTertiary} />
                          <span style={{ fontSize: 11, color: theme.textTertiary, width: 110 }}>{label}</span>
                          <span style={{ fontSize: 12, color: theme.textPrimary, fontWeight: 500 }}>{value}</span>
                        </div>
                      ))}

                      <button onClick={e => openEdit(e, manager)} style={{ ...primaryBtn, marginTop: 10 }}>
                        <Pencil size={12} /> Edit Fund Manager
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: theme.bgBase, borderBottom: `1px solid ${theme.border}` }}>
                {['Manager', 'Country', 'CSSF Registration', 'Registered', ''].map(h => (
                  <th key={h} style={{
                    padding: '11px 16px',
                    textAlign: 'left',
                    fontSize: 10,
                    fontWeight: 600,
                    color: theme.textTertiary,
                    textTransform: 'uppercase',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((manager, idx) => (
                <tr key={manager.id} style={{
                  borderBottom: idx < filtered.length - 1 ? `1px solid ${theme.border}` : 'none',
                  transition: 'background 0.12s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = theme.bgCardHover}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '14px 16px' }}>
                    <div>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>{manager.legal_name}</p>
                      <p style={{ margin: '2px 0 0', fontSize: 11, color: theme.textTertiary }}>{manager.contact_email}</p>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <Badge theme={theme}>{COUNTRY_LABEL[manager.country] || manager.country || '-'}</Badge>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: 12, color: theme.textSecondary }}>{manager.cssf_registration || '-'}</td>
                  <td style={{ padding: '14px 16px', fontSize: 12, color: theme.textSecondary }}>
                    {manager.created_at ? new Date(manager.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                    <button onClick={e => openEdit(e, manager)} style={secondaryIconBtn}>
                      <Pencil size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <Modal
          theme={theme}
          isMobile={isMobile}
          isEdit={false}
          title="Add fund manager"
          subtitle="Register a new fund management company."
          onClose={closeCreate}
          onSubmit={handleCreate}
          submitting={creating}
          error={createError}
          form={createForm}
          setForm={setCreateForm}
        />
      )}

      {showEdit && (
        <Modal
          theme={theme}
          isMobile={isMobile}
          isEdit={true}
          title="Edit fund manager"
          subtitle={`Editing ${editTarget?.legal_name}`}
          onClose={closeEdit}
          onSubmit={handleEdit}
          submitting={editing}
          error={editError}
          form={editForm}
          setForm={setEditForm}
        />
      )}
    </div>
  );
};

export default FundManagers;
